package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.LayoutDirection
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.materialSymbolsFamily
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.materialSymbolsFilledFamily

/**
 * Directional glyphs that must mirror under RTL (Farsi). Font ligatures are TEXT — Compose's
 * layout mirroring never flips them, so a "back" arrow kept pointing left in RTL. Mirroring is
 * applied centrally here so every screen is correct without per-call-site work.
 */
private val RtlMirroredIcons = setOf(
    "arrow_back", "arrow_forward", "arrow_back_ios", "arrow_forward_ios",
    "arrow_right_alt", "arrow_left_alt", "chevron_left", "chevron_right",
    "chevron_forward", "chevron_backward", "navigate_next", "navigate_before",
    "keyboard_backspace", "first_page", "last_page", "login", "logout",
    "send", "reply", "start",
)

/**
 * Renders a Material Symbols Outlined icon by its **ligature name** — identical to how the Stitch
 * design renders icons (`<span class="material-symbols-outlined">restaurant</span>`).
 * Copy the [name] straight from the Stitch HTML of the screen you're building.
 *
 * @param name Material Symbols ligature, e.g. "search", "restaurant", "verified", "call", "directions".
 * @param filled render the FILL=1 variant — matches Stitch's `font-variation-settings: 'FILL' 1`
 *   (rating stars, active favorites, verified badges, the selected bottom-nav icon).
 * @param contentDescription localized a11y label (string resource); null = decorative.
 */
@Composable
fun AppIcon(
    name: String,
    modifier: Modifier = Modifier,
    size: Dp = AppTheme.sizes.iconMd,
    tint: Color = LocalContentColor.current,
    filled: Boolean = false,
    contentDescription: String? = null,
) {
    val fontSize = with(LocalDensity.current) { size.toSp() }
    val mirror = name in RtlMirroredIcons && LocalLayoutDirection.current == LayoutDirection.Rtl
    Text(
        text = name,
        // A ligature glyph is TEXT — without clearing semantics, a "decorative" icon reads its raw
        // ligature name ("chevron_forward") to TalkBack/VoiceOver in every language. Cleared AFTER
        // the caller's modifier so an outer clickable's own semantics survive.
        modifier = (if (contentDescription != null) {
            modifier.semantics { this.contentDescription = contentDescription }
        } else {
            modifier.clearAndSetSemantics { }
        }).then(if (mirror) Modifier.graphicsLayer { scaleX = -1f } else Modifier),
        style = TextStyle(
            fontFamily = if (filled) materialSymbolsFilledFamily() else materialSymbolsFamily(),
            fontSize = fontSize,
            color = tint,
        ),
    )
}

/**
 * Tokenized icon-only action. [contentDescription] must be localized by the caller; unlike a
 * decorative [AppIcon], an interactive icon always exposes an accessibility label.
 */
@Composable
fun AppIconButton(
    iconName: String,
    contentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    filled: Boolean = false,
    tint: Color = AppTheme.colors.primary,
) {
    IconButton(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled,
        colors = IconButtonDefaults.iconButtonColors(
            contentColor = tint,
            disabledContentColor = AppTheme.colors.outlineVariant,
        ),
    ) {
        AppIcon(
            name = iconName,
            size = AppTheme.sizes.iconMd,
            filled = filled,
            contentDescription = contentDescription,
        )
    }
}
